import { useEffect, useState } from 'react';
import { useIxfHoursV1OperatingHoursServiceGetOperatingHours } from '@interxfi/api-client/generated/hooks/ixf-hours-v1-operating-hours-service-get-operating-hours';
import { usePreferredTimeZone } from '@interxfi/design-system/time-zone-context';
import { useCurrentDate } from '@interxfi/design-system/util';
import type { IxfHoursV1OpenClose, IxfHoursV1OperatingDay, IxfHoursV1OperatingHours, IxfHoursV1SessionHours } from '@interxfi/types';
import type { IxfHoursV1Region } from '@interxfi/types/generated/api';
import type { ScheduleData, ScheduleName, ScheduleSession } from './helpers';

export type ScheduleOpenStatus = 'loading' | 'open' | 'closed';

export function getScheduleSessions<T extends ScheduleName>(
  schedule: T,
  operatingDay: IxfHoursV1OperatingDay | undefined,
): ScheduleData[T] | undefined {
  return operatingDay?.[schedule] as ScheduleData[T] | undefined;
}

export function getScheduleSession<T extends ScheduleName>(
  schedule: T,
  session: ScheduleSession<T>,
  operatingDay: IxfHoursV1OperatingDay | undefined,
): IxfHoursV1SessionHours | undefined {
  return getScheduleSessions(schedule, operatingDay)?.[session] as IxfHoursV1SessionHours | undefined;
}

export function getOpenWindow(hours: IxfHoursV1OperatingHours | undefined): IxfHoursV1OpenClose | undefined {
  return hours && 'open' in hours ? hours.open : undefined;
}

export function useIsScheduleOpen<T extends ScheduleName>(
  enabled: boolean,
  region: IxfHoursV1Region,
  schedule: T,
  session: ScheduleSession<T>,
  timeZone?: string,
): ScheduleOpenStatus {
  const [isOpen, setIsOpen] = useState(false);
  const preferredTimeZone = usePreferredTimeZone();
  const scheduleTimeZone = timeZone ?? preferredTimeZone;
  const { dateAsString: currentDate } = useCurrentDate(scheduleTimeZone);
  const { data, isPending, error } = useIxfHoursV1OperatingHoursServiceGetOperatingHours(
    enabled ? { date: currentDate, timezone: scheduleTimeZone, key: region } : undefined,
    {
      refetchOnWindowFocus: false,
      refetchInterval: false,
    },
  );

  const day = data?.today;
  const openWindow = getOpenWindow(getScheduleSession(schedule, session, day)?.effective);
  const dayDate = day?.date;
  const opensAtIso = openWindow?.opensAt;
  const closesAtIso = openWindow?.closesAt;

  useEffect(() => {
    if (dayDate !== currentDate || !opensAtIso || !closesAtIso) {
      setIsOpen(false);
      return;
    }

    const opensAt = new Date(opensAtIso).valueOf();
    const closesAt = new Date(closesAtIso).valueOf();

    if (Number.isNaN(opensAt) || Number.isNaN(closesAt)) {
      setIsOpen(false);
      return;
    }

    function updateOpenState() {
      const now = Date.now();
      setIsOpen(now >= opensAt && now < closesAt);
    }

    updateOpenState();

    const now = Date.now();
    const nextTransition = now < opensAt ? opensAt : now < closesAt ? closesAt : undefined;

    if (nextTransition === undefined) {
      return;
    }

    const timeoutHandle = window.setTimeout(updateOpenState, Math.max(0, nextTransition - now));

    return () => {
      window.clearTimeout(timeoutHandle);
    };
  }, [currentDate, dayDate, opensAtIso, closesAtIso]);

  return isOpen ? 'open' : isPending || error ? 'loading' : 'closed';
}
